/**
 * Tâche 1315 (Eric 2026-06-30) — Enrichissement en lot, sur un écran unique.
 *
 * Trois portées au choix (parcours, thème, jamais passé), une estimation du
 * coût AVANT de lancer, puis toutes les propositions groupées par TERME avec
 * l'avant/après et des cases à cocher.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

import { API_BASE as API } from "../lib/apiBase";
import { useLang } from "../contexts/LanguageContext";

export default function JargonEnrichmentBatchPage() {
  const { tr } = useLang();
  const [mode, setMode] = useState("never");
  const [pathways, setPathways] = useState([]);
  const [pathwayId, setPathwayId] = useState("");
  const [themes, setThemes] = useState([]);
  const [theme, setTheme] = useState("");
  const [scope, setScope] = useState(null);
  const [loadingScope, setLoadingScope] = useState(false);
  const [batch, setBatch] = useState(null);
  const [groups, setGroups] = useState([]);
  const [checked, setChecked] = useState({});
  const [applying, setApplying] = useState(false);
  const [confirmLot, setConfirmLot] = useState(false);
  const [confirmEcart, setConfirmEcart] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, v] = await Promise.all([
          axios.get(`${API}/api/pathways`, { withCredentials: true }),
          axios.get(`${API}/api/videos?limit=200`, { withCredentials: true }),
        ]);
        setPathways(p.data?.items || p.data || []);
        const t = new Set();
        (v.data?.items || []).forEach((x) => x.training_theme && t.add(x.training_theme));
        setThemes([...t].sort());
      } catch (_) { /* écran utilisable sans ces listes */ }
    })();
  }, []);

  const chargerPropositions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/jargon/enrichment/proposals`, { withCredentials: true });
      setGroups(data?.groups || []);
    } catch (e) {
      toast.error(tr("Chargement des propositions impossible : ", "Could not load the proposals: ") + (e?.response?.data?.detail || e.message));
    }
  }, []);

  useEffect(() => { chargerPropositions(); }, [chargerPropositions]);


  const verifierPortee = async () => {
    setLoadingScope(true);
    try {
      const { data } = await axios.get(`${API}/api/jargon/enrichment/scope`, {
        params: { mode, pathway_id: pathwayId, theme },
        withCredentials: true,
      });
      setScope(data);
    } catch (e) {
      setScope(null);
      toast.error(tr("Portée impossible : ", "Scope failed: ") + (e?.response?.data?.detail || e.message));
    } finally {
      setLoadingScope(false);
    }
  };

  // Confirmation DANS l'écran (choix d'Eric : le montant doit être montré
  // avant de lancer). Une fenêtre système serait hors charte et invisible des
  // tests ; ici la dépense reste lisible à côté du bouton.
  const lancerLot = async () => {
    if (!scope?.count) return;
    if (!confirmLot) { setConfirmLot(true); return; }
    setConfirmLot(false);
    try {
      const { data } = await axios.post(`${API}/api/jargon/enrichment/batch`,
        { video_ids: scope.documents.map((d) => d.id), mode },
        { withCredentials: true });
      setBatch(data.batch);
      toast.success(`Lot lancé sur ${data.batch.total} document(s).`);
    } catch (e) {
      toast.error("Lancement impossible : " + (e?.response?.data?.detail || e.message));
    }
  };

  // Suivi du lot : un lot de 40 documents dure plusieurs minutes, l'écran doit
  // dire où il en est plutôt que de rester figé.
  useEffect(() => {
    if (!batch?.id || batch.status === "done") return undefined;
    const timer = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/jargon/enrichment/batch/${batch.id}`, { withCredentials: true });
        setBatch(data);
        if (data.status === "done") {
          toast.success(`Lot terminé : ${data.proposals_count} proposition(s).`);
          chargerPropositions();
        }
      } catch (_) { /* on retentera au prochain tour */ }
    }, 4000);
    return () => clearInterval(timer);
  }, [batch, chargerPropositions]);

  const cle = (p) => `${p.video_id}::${p.entry_id}`;
  const cochees = Object.keys(checked).filter((k) => checked[k]);

  const toutCocher = (valeur) => {
    const suivant = {};
    groups.forEach((g) => g.proposals.forEach((p) => { suivant[cle(p)] = valeur; }));
    setChecked(suivant);
  };

  const appliquer = async () => {
    if (!cochees.length) return;
    setApplying(true);
    try {
      const selections = cochees.map((k) => {
        const [video_id, entry_id] = k.split("::");
        return { video_id, entry_id };
      });
      const { data } = await axios.post(`${API}/api/jargon/enrichment/apply`,
        { selections }, { withCredentials: true });
      toast.success(`${data.applied_count} terme(s) enrichi(s) — documents ajoutés à leurs sources.`);
      setChecked({});
      await chargerPropositions();
    } catch (e) {
      toast.error("Enrichissement impossible : " + (e?.response?.data?.detail || e.message));
    } finally {
      setApplying(false);
    }
  };

  const ecarterTout = async () => {
    if (!confirmEcart) { setConfirmEcart(true); return; }
    setConfirmEcart(false);
    try {
      await axios.delete(`${API}/api/jargon/enrichment/proposals`, {
        data: {}, withCredentials: true,
      });
      setChecked({});
      await chargerPropositions();
      toast.success(tr("Propositions écartées.", "Proposals discarded."));
    } catch (e) {
      toast.error(tr("Impossible d'écarter : ", "Could not discard: ") + (e?.response?.data?.detail || e.message));
    }
  };

  const total = groups.reduce((n, g) => n + g.proposals.length, 0);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="jargon-enrichment-batch-page">
      <h1 className="text-2xl font-bold text-zinc-900">{tr("Enrichissement en lot", "Bulk enrichment")}</h1>
      <p className="text-sm text-zinc-600 mt-1 max-w-3xl">
        {tr(
          "Passe plusieurs documents d'un coup, puis rassemble toutes les propositions sur cet écran, groupées par terme. Un appel IA par document : la dépense vous est montrée avant de lancer.",
          "Runs through several documents at once, then gathers every proposal on this screen, grouped by term. One AI call per document: the cost is shown before you start.",
        )}
      </p>

      <section className="mt-6 rounded-xl border border-zinc-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs font-medium text-zinc-700">
            {tr("Portée", "Scope")}
            <select
              value={mode}
              onChange={(e) => { setMode(e.target.value); setScope(null); }}
              className="block mt-1 text-sm border border-zinc-300 rounded-md px-2 py-1.5"
              data-testid="batch-mode-select"
            >
              <option value="never">{tr("Jamais passé", "Never processed")}</option>
              <option value="pathway">{tr("Un parcours", "One pathway")}</option>
              <option value="theme">{tr("Un thème", "One theme")}</option>
            </select>
          </label>
          {mode === "pathway" && (
            <label className="text-xs font-medium text-zinc-700">
              {tr("Parcours", "Pathway")}
              <select
                value={pathwayId}
                onChange={(e) => { setPathwayId(e.target.value); setScope(null); }}
                className="block mt-1 text-sm border border-zinc-300 rounded-md px-2 py-1.5 max-w-xs"
                data-testid="batch-pathway-select"
              >
                <option value="">{tr("— choisir —", "— pick one —")}</option>
                {pathways.map((p) => (
                  <option key={p.id} value={p.id}>{p.name || p.title || p.id}</option>
                ))}
              </select>
            </label>
          )}
          {mode === "theme" && (
            <label className="text-xs font-medium text-zinc-700">
              {tr("Thème", "Theme")}
              <select
                value={theme}
                onChange={(e) => { setTheme(e.target.value); setScope(null); }}
                className="block mt-1 text-sm border border-zinc-300 rounded-md px-2 py-1.5 max-w-xs"
                data-testid="batch-theme-select"
              >
                <option value="">{tr("— choisir —", "— pick one —")}</option>
                {themes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
          )}
          <button
            onClick={verifierPortee}
            disabled={loadingScope}
            className="px-3 py-2 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-100 text-xs font-bold disabled:opacity-50"
            data-testid="batch-scope-btn"
          >
            {loadingScope ? "Calcul…" : "Voir les documents et le coût"}
          </button>
        </div>

        {scope && (
          <div className="mt-4 rounded-lg border border-zinc-200 bg-zinc-50 p-3" data-testid="batch-scope-summary">
            <p className="text-sm text-zinc-800">
              <span className="font-bold">{scope.count}</span> document(s) concerné(s)
              {scope.count > 0 && (
                <>
                  {" — coût estimé "}
                  <span className="font-bold" data-testid="batch-cost-total">{scope.cost.total_usd} $ US</span>
                  {` (${scope.cost.per_document_usd} $ par document, tarifs au ${scope.cost.prices_as_of})`}
                </>
              )}
            </p>
            <p className="text-[11px] text-zinc-500 mt-1">{scope.cost.note}</p>
            {scope.count > scope.max_per_batch - 1 && (
              <p className="text-[11px] text-amber-700 mt-1">
                Maximum {scope.max_per_batch} documents par lot : relancez pour la suite.
              </p>
            )}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <button
                onClick={lancerLot}
                disabled={!scope.count}
                className={`px-3 py-2 rounded-lg text-white text-xs font-bold disabled:opacity-50 ${
                  confirmLot ? "bg-amber-700 hover:bg-amber-800" : "bg-[#002FA7] hover:bg-[#002587]"
                }`}
                data-testid="batch-run-btn"
              >
                {confirmLot
                  ? `Confirmer : ${scope.count} document(s), ${scope.cost.total_usd} $ US estimés`
                  : "Lancer le lot"}
              </button>
              {confirmLot && (
                <button
                  onClick={() => setConfirmLot(false)}
                  className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-xs font-bold text-zinc-700"
                  data-testid="batch-run-cancel"
                >
                  {tr("Annuler", "Cancel")}
                </button>
              )}
            </div>
          </div>
        )}

        {batch && (
          <div className="mt-3 text-xs text-zinc-700" data-testid="batch-progress">
            {tr("Lot ", "Batch ")}{batch.status === "done" ? tr("terminé", "finished") : tr("en cours", "running")}
            {" : "}{batch.done} / {batch.total} {tr("document(s)", "document(s)")},
            {" "}{batch.proposals_count} {tr("proposition(s)", "proposal(s)")}.
            {(batch.errors || []).length > 0 && (
              <span className="text-amber-700">
                {" "}{batch.errors.length} {tr("document(s) en échec (le lot continue).", "document(s) failed (the batch carries on).")}
              </span>
            )}
          </div>
        )}
      </section>

      <section className="mt-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-base font-bold text-zinc-900">
            {tr("Propositions à valider", "Proposals to review")} ({total})
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={() => toutCocher(true)} className="text-xs underline text-zinc-700" data-testid="batch-check-all">{tr("Tout cocher", "Tick all")}</button>
            <button onClick={() => toutCocher(false)} className="text-xs underline text-zinc-700" data-testid="batch-uncheck-all">{tr("Tout décocher", "Untick all")}</button>
            <button onClick={ecarterTout} className="text-xs underline text-zinc-500" data-testid="batch-discard-all">
              {confirmEcart ? tr("Confirmer l'écartement", "Confirm discarding") : tr("Écarter tout", "Discard all")}
            </button>
            <button
              onClick={appliquer}
              disabled={applying || !cochees.length}
              className="px-3 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold disabled:opacity-50"
              data-testid="batch-apply-btn"
            >
              {applying ? tr("Enrichissement…", "Enriching…") : tr(`Enrichir (${cochees.length})`, `Enrich (${cochees.length})`)}
            </button>
          </div>
        </div>

        {total === 0 ? (
          <p className="mt-3 text-sm text-zinc-600" data-testid="batch-empty">
            {tr("Aucune proposition en attente. Lancez un lot ci-dessus.", "No proposal waiting. Start a batch above.")}
          </p>
        ) : (
          <ul className="mt-3 space-y-4">
            {groups.map((g) => (
              <li key={g.term} className="rounded-xl border border-zinc-200 bg-white p-4" data-testid={`batch-group-${g.term}`}>
                <h3 className="text-sm font-bold text-zinc-900">{g.term}</h3>
                <ul className="mt-2 space-y-2">
                  {g.proposals.map((p) => (
                    <li key={cle(p)} className="rounded border border-zinc-200 bg-zinc-50 p-3">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!checked[cle(p)]}
                          onChange={(e) => setChecked((prev) => ({ ...prev, [cle(p)]: e.target.checked }))}
                          className="mt-0.5 h-3.5 w-3.5 accent-emerald-600"
                          data-testid={`batch-check-${p.entry_id}`}
                        />
                        <span className="text-[11px] text-zinc-700">
                          Source : <span className="font-medium">{p.document_title}</span>
                          {p.document_kind === "text" ? " (texte)" : " (vidéo)"}
                          {p.already_source ? " — déjà dans les sources" : " — sera ajoutée aux sources"}
                        </span>
                      </label>
                      {p.reason && <p className="mt-1 pl-6 text-[11px] text-zinc-600">{p.reason}</p>}
                      <div className="mt-2 pl-6 grid gap-2 md:grid-cols-2">
                        <div className="rounded border border-zinc-200 bg-white p-2">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">{tr("Avant", "Before")}</div>
                          <div className="mt-1 text-[11px] text-zinc-700 whitespace-pre-wrap break-words">
                            {p.definition_before || tr("(aucune définition)", "(no definition)")}
                          </div>
                        </div>
                        <div className="rounded border border-emerald-300 bg-emerald-50/70 p-2">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">{tr("Après", "After")}</div>
                          <div className="mt-1 text-[11px] text-emerald-900 whitespace-pre-wrap break-words">
                            {p.definition_after}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
